import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

/**
 * A portal-rendered floating panel anchored to a trigger element. Renders into
 * document.body so it escapes any `overflow:hidden/auto` clipping (queue scroll,
 * insights drawer, etc.). Closes on outside-click and Escape.
 *
 * Because the panel is `position: fixed`, scrolling or resizing moves the anchor
 * out from under it; it follows the anchor instead of closing, and only closes
 * once the anchor itself has scrolled out of sight.
 *
 * Usage:
 *   <Trigger innerRef={(n) => { this.anchor = n; }} onClick={this.toggle} />
 *   <Popover open={this.state.open} getAnchor={() => this.anchor}
 *            onClose={this.close} align="right">…</Popover>
 */
class Popover extends React.Component {
  state = { pos: null };

  componentDidMount() {
    if (this.props.open) this.bind();
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) this.bind();
    if (!this.props.open && prevProps.open) this.unbind();
  }

  componentWillUnmount() {
    this.unbind();
  }

  setMenuRef = (node) => { this.menuNode = node; };

  bind = () => {
    this.reposition();
    document.addEventListener('mousedown', this.onOutside, true);
    document.addEventListener('keydown', this.onKey, true);
    window.addEventListener('scroll', this.onScrollResize, true);
    window.addEventListener('resize', this.onScrollResize);
  };

  unbind = () => {
    document.removeEventListener('mousedown', this.onOutside, true);
    document.removeEventListener('keydown', this.onKey, true);
    window.removeEventListener('scroll', this.onScrollResize, true);
    window.removeEventListener('resize', this.onScrollResize);
  };

  // Scrolling the panel's own option list must not move the panel. The containment check
  // is only meaningful when the event came from a node: a `resize`, or a `scroll`
  // dispatched at `window` rather than at a scrolling element, targets `window` itself,
  // which is not a Node, and `contains` throws a TypeError for one instead of returning
  // false.
  onScrollResize = (e) => {
    const target = e && e.target;
    const insideOwnPanel = this.menuNode && target instanceof Node && this.menuNode.contains(target);
    if (insideOwnPanel) return;
    const anchor = this.props.getAnchor && this.props.getAnchor();
    if (!anchor) return;
    if (this.isAnchorHidden(anchor)) this.props.onClose();
    else this.reposition();
  };

  // True once the trigger has scrolled past the window edge or past the edge of
  // a scrolling ancestor (e.g. a row menu whose row left the list viewport):
  // keeping the panel open there would leave it floating over unrelated content.
  isAnchorHidden = (anchor) => {
    // A viewport with no area puts every anchor "outside" it, which says nothing about
    // the anchor, so it must not close the panel.
    if (!window.innerWidth || !window.innerHeight) return false;
    const r = anchor.getBoundingClientRect();
    const outside = (a, b) => (
      r.bottom <= b.top || r.top >= b.bottom || r.right <= b.left || r.left >= b.right
    );
    if (outside(r, { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth })) {
      return true;
    }
    let el = anchor.parentElement;
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const clips = /auto|scroll|hidden/.test(`${style.overflowY} ${style.overflowX}`);
      if (clips && outside(r, el.getBoundingClientRect())) return true;
      el = el.parentElement;
    }
    return false;
  };

  onKey = (e) => { if (e.key === 'Escape') this.props.onClose(); };

  onOutside = (e) => {
    const anchor = this.props.getAnchor && this.props.getAnchor();
    if (this.menuNode && this.menuNode.contains(e.target)) return;
    if (anchor && anchor.contains(e.target)) return;
    this.props.onClose();
  };

  reposition = () => {
    const anchor = this.props.getAnchor && this.props.getAnchor();
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const pos = {
      top: r.bottom + this.props.gap,
      left: r.left,
      right: window.innerWidth - r.right
    };
    // Scroll fires per frame, so bail out rather than re-render on every event
    // when the anchor has not actually moved (e.g. a sticky filter bar).
    const prev = this.state.pos;
    if (prev && prev.top === pos.top && prev.left === pos.left && prev.right === pos.right) return;
    this.setState({ pos });
  };

  render() {
    const { open, align, width, children } = this.props;
    const { pos } = this.state;
    if (!open || !pos) return null;

    const style = { position: 'fixed', top: pos.top, zIndex: 4000 };
    if (align === 'right') style.right = pos.right;
    else style.left = pos.left;
    if (width) style.minWidth = width;

    return ReactDOM.createPortal(
      <div ref={this.setMenuRef} style={style}>{children}</div>,
      document.body
    );
  }
}

Popover.propTypes = {
  open: PropTypes.bool.isRequired,
  getAnchor: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  align: PropTypes.oneOf(['left', 'right']),
  width: PropTypes.string,
  gap: PropTypes.number,
  children: PropTypes.node
};

Popover.defaultProps = {
  align: 'left',
  width: null,
  gap: 4,
  children: null
};

export default Popover;
